import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Empty, Spin, Table, Tag, Select } from 'antd';
import { Section, Card, TableWrapper, PriceDisplay } from '../styles';
import useIsMobile from '../../../hooks/useIsMobile';
import { MobileFilterBar, FilterGroup } from '../../../components/mobile';
import apiService from '../../../services/api';
import { ServiceType } from '../../../services/api';
import type { OrdersResponseDto, OrderItemDto } from '../../../api-schema/generated/types';
import { usePortfolios } from '../../../hooks/usePortfolios';
import styled from 'styled-components';
import { formatNumber } from '../../../utils/numberFormat';

interface Portfolio {
    id: string;
    portfolioId: string;
    portfolioName: string;
    createdDate: string;
    status: 'active' | 'inactive' | 'closed';
}

export interface OrderAndTransactionsTabProps {
    selectedPortfolio: Portfolio | undefined;
    active?: boolean;
    refreshKey?: number;
    /** Re-fetch every N ms while the tab is active — set while the bot runs, so new orders
     *  and fills appear without a reload. Background polls show no spinner. */
    pollMs?: number;
}

const coreService = apiService.getService(ServiceType.CORE);

const FILTER_DROPDOWN_STYLES = `
    .ant-table-filter-dropdown {
        background: #1E2329 !important;
        border: 1px solid #2B3139 !important;
        border-radius: 6px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
    }
    .ant-table-filter-dropdown .ant-dropdown-menu {
        background: #1E2329 !important;
    }
    .ant-table-filter-dropdown .ant-dropdown-menu-item {
        color: #EAECEF !important;
    }
    .ant-table-filter-dropdown .ant-dropdown-menu-item:hover {
        background: #2B3139 !important;
    }
    .ant-table-filter-dropdown .ant-table-filter-dropdown-btns {
        border-top: 1px solid #2B3139 !important;
        background: #1A1D23 !important;
    }
    .ant-table-filter-dropdown .ant-btn-link {
        color: #848E9C !important;
    }
    .ant-table-filter-dropdown .ant-btn-link:hover {
        color: #EAECEF !important;
    }
    .ant-table-filter-dropdown .ant-btn-primary {
        background: #0ECB81 !important;
        border-color: #0ECB81 !important;
        color: #fff !important;
    }
    .ant-table-filter-dropdown .ant-btn-primary:hover {
        background: #0bb369 !important;
        border-color: #0bb369 !important;
    }
    .ant-table-filter-dropdown .ant-checkbox-wrapper {
        color: #EAECEF !important;
    }
    .ant-table-filter-dropdown .ant-checkbox-inner {
        background: #2B3139 !important;
        border-color: #484E56 !important;
    }
    .ant-table-filter-dropdown .ant-checkbox-checked .ant-checkbox-inner {
        background: #0ECB81 !important;
        border-color: #0ECB81 !important;
    }
    .ant-table-filter-dropdown .ant-table-filter-dropdown-search {
        border-bottom: 1px solid #2B3139 !important;
    }
    /* The search box is an affix wrapper (icon + input): colour the wrapper, not the
       bare input, or the input shows as a lighter box inside a dark frame. */
    .ant-table-filter-dropdown .ant-input-affix-wrapper {
        background: #181A20 !important;
        border-color: #2B3139 !important;
        box-shadow: none !important;
    }
    .ant-table-filter-dropdown .ant-input-affix-wrapper:hover,
    .ant-table-filter-dropdown .ant-input-affix-wrapper-focused {
        border-color: #0ECB81 !important;
    }
    .ant-table-filter-dropdown .ant-input-affix-wrapper .ant-input {
        background: transparent !important;
        color: #EAECEF !important;
    }
    .ant-table-filter-dropdown .ant-input::placeholder {
        color: #5E6673 !important;
    }
    .ant-table-filter-dropdown .ant-input-prefix {
        color: #848E9C !important;
    }
    .ant-table-filter-trigger {
        color: #848E9C !important;
    }
    .ant-table-filter-trigger:hover {
        color: #EAECEF !important;
    }
    .ant-table-filter-trigger.active {
        color: #0ECB81 !important;
    }
`;

const StyledTableWrapper = styled.div`
    .ant-pagination {
        .ant-pagination-item {
            border-color: ${({ theme }) => theme.colors.border.primary};
            background-color: ${({ theme }) => theme.colors.background.secondary};
            
            a {
                color: ${({ theme }) => theme.colors.text.secondary};
            }
            
            &:hover {
                border-color: ${({ theme }) => theme.colors.trading.buy};
                
                a {
                    color: ${({ theme }) => theme.colors.trading.buy};
                }
            }
        }
        
        .ant-pagination-item-active {
            border-color: ${({ theme }) => theme.colors.trading.buy};
            background-color: ${({ theme }) => theme.colors.trading.buy};
            
            a {
                color: ${({ theme }) => theme.colors.background.primary};
            }
            
            &:hover {
                border-color: ${({ theme }) => theme.colors.trading.buy};
                background-color: ${({ theme }) => theme.colors.trading.buy};
                
                a {
                    color: ${({ theme }) => theme.colors.background.primary};
                }
            }
        }
        
        .ant-pagination-prev,
        .ant-pagination-next {
            .ant-pagination-item-link {
                border-color: ${({ theme }) => theme.colors.border.primary};
                background-color: ${({ theme }) => theme.colors.background.secondary};
                color: ${({ theme }) => theme.colors.text.secondary};
                
                &:hover {
                    border-color: ${({ theme }) => theme.colors.trading.buy};
                    color: ${({ theme }) => theme.colors.trading.buy};
                }
            }
        }
        
        .ant-pagination-jump-prev,
        .ant-pagination-jump-next {
            .ant-pagination-item-link {
                border-color: ${({ theme }) => theme.colors.border.primary};
                color: ${({ theme }) => theme.colors.text.secondary};
            }
        }
        
        .ant-select-selector {
            border-color: ${({ theme }) => theme.colors.border.primary};
            background-color: ${({ theme }) => theme.colors.background.secondary};
            color: ${({ theme }) => theme.colors.text.primary};
        }
    }
`;

interface DisplayOrder {
    orderId: string;
    clOrdId: string;
    symbol: string;
    securityExchange: string;
    side: 'BUY' | 'SELL';
    price: number;
    orderQty: number;
    cumQty: number;
    leavesQty: number;
    avgPx: number;
    statusCode: string;
    isCancelled: boolean;
    orderDate: string;
    lastUpdateDate: string;
    createdAt: string;
}

const mapOrderItem = (item: OrderItemDto): DisplayOrder => ({
    orderId: item.orderId,
    clOrdId: item.clOrdId,
    symbol: item.symbol,
    securityExchange: item.securityExchange,
    side: item.side === '1' ? 'BUY' : 'SELL',
    price: item.price,
    orderQty: item.orderQty,
    cumQty: item.cumQty,
    leavesQty: item.leavesQty,
    avgPx: item.avgPx,
    statusCode: item.statusCode || item.ordStatus || 'UNKNOWN',
    isCancelled: item.isCancelled,
    orderDate: item.orderDate,
    lastUpdateDate: item.lastUpdateDate,
    createdAt: item.createdAt,
});

const ORDER_STATUS_CONFIG: Record<string, { color: string; text: string }> = {
    'RECEIVED_BROKER': { color: 'blue', text: 'Received Broker' },
    'STOCK_EXCHANGE_OK': { color: 'cyan', text: 'Stock Exchange OK' },
    'THIRD_PARTY_OK': { color: 'cyan', text: 'Third Party OK' },
    'PARTIALLY_FILLED': { color: 'orange', text: 'Partially Filled' },
    'FILLED': { color: 'green', text: 'Filled' },
    'PENDING_CANCELED': { color: 'gold', text: 'Pending Canceled' },
    'PENDING_CANCEL': { color: 'gold', text: 'Pending Cancel' },
    'CANCELED': { color: 'red', text: 'Canceled' },
    'EXPIRED': { color: 'default', text: 'Expired' },
    'PENDING': { color: 'blue', text: 'Pending' },
    'REJECTED': { color: 'red', text: 'Rejected' },
};

const SIDE_FILTERS = [
    { text: 'BUY', value: 'BUY' },
    { text: 'SELL', value: 'SELL' },
];

const statusText = (code: string): string => ORDER_STATUS_CONFIG[code]?.text ?? code;

/** Statuses on screen, so a code no config knows (each broker spells its own) is still filterable. */
const statusFiltersFor = (orders: DisplayOrder[]) =>
    Array.from(new Set(orders.map((o) => o.statusCode)))
        .sort((a, b) => statusText(a).localeCompare(statusText(b)))
        .map((code) => ({ text: statusText(code), value: code }));

const symbolFiltersFor = (orders: DisplayOrder[]) =>
    Array.from(new Set(orders.map((o) => o.symbol).filter(Boolean)))
        .sort()
        .map((sym) => ({ text: sym, value: sym }));

const formatTimeOnly = (time: string): string => {
    if (!time) return '-';
    // Append 'Z' if no timezone info so the string is parsed as UTC
    const normalized = /[Zz]|[+-]\d{2}:?\d{2}$/.test(time) ? time : time + 'Z';
    const date = new Date(normalized);
    if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Asia/Ho_Chi_Minh' });
    }
    // Fallback: extract time portion from string like "2024-01-01 09:30:00"
    const match = time.match(/(\d{2}:\d{2}(:\d{2})?)/);
    return match ? match[1] : time;
};

const buildOrderColumns = (orders: DisplayOrder[]) => [
    {
        title: 'Symbol',
        dataIndex: 'symbol',
        key: 'symbol',
        filters: symbolFiltersFor(orders),
        filterSearch: true,
        onFilter: (value: any, record: DisplayOrder) => record.symbol === value,
    },
    {
        title: 'Side',
        dataIndex: 'side',
        key: 'side',
        filters: SIDE_FILTERS,
        onFilter: (value: any, record: DisplayOrder) => record.side === value,
        render: (side: string) => (
            <Tag color={side === 'BUY' ? 'green' : 'red'}>{side}</Tag>
        ),
    },
    {
        title: 'Status',
        dataIndex: 'statusCode',
        key: 'statusCode',
        filters: statusFiltersFor(orders),
        onFilter: (value: any, record: DisplayOrder) => record.statusCode === value,
        render: (status: string) => {
            const cfg = ORDER_STATUS_CONFIG[status] || { color: 'default', text: status };
            return <Tag color={cfg.color}>{cfg.text}</Tag>;
        },
    },
    {
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        align: 'right' as const,
        render: (price: number) => (
            <PriceDisplay size="small">{price > 0 ? formatNumber(price) : '-'}</PriceDisplay>
        ),
    },
    {
        title: 'Qty',
        dataIndex: 'orderQty',
        key: 'orderQty',
        align: 'right' as const,
    },
    {
        title: 'Filled',
        dataIndex: 'cumQty',
        key: 'cumQty',
        align: 'right' as const,
        render: (filled: number) => (
            <span style={{ color: filled > 0 ? '#0ECB81' : '#848E9C' }}>{filled}</span>
        ),
    },
    {
        title: 'Remain',
        dataIndex: 'leavesQty',
        key: 'leavesQty',
        align: 'right' as const,
        render: (remain: number) => (
            <span style={{ color: remain > 0 ? '#FCD535' : '#848E9C' }}>{remain}</span>
        ),
    },
    {
        title: 'Avg Price',
        dataIndex: 'avgPx',
        key: 'avgPx',
        align: 'right' as const,
        render: (price: number) => (
            <PriceDisplay size="small">{price > 0 ? formatNumber(price) : '-'}</PriceDisplay>
        ),
    },
    {
        title: 'Time',
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: (a: DisplayOrder, b: DisplayOrder) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        defaultSortOrder: 'descend' as const,
        render: (time: string) => formatTimeOnly(time),
    },
];


/**
 * One order per card instead of one per table row.
 *
 * The table carries nine columns — symbol, side, status, price, qty, filled, remain, avg
 * price, time. At phone width that is a horizontal scroll where the columns that matter
 * (filled, remain) sit off-screen, and antd's per-column filter dropdowns are not reachable
 * with a thumb. The same nine values fit a three-line card, with side and status as the
 * chips the eye lands on first.
 */
const MobileOrderList: React.FC<{ orders: DisplayOrder[]; loading: boolean }> = ({
    orders,
    loading,
}) => {
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
            </div>
        );
    }
    if (orders.length === 0) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No orders" />;
    }
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {orders.map((o) => {
                const cfg = ORDER_STATUS_CONFIG[o.statusCode] || { color: 'default', text: o.statusCode };
                return (
                    <div
                        key={o.orderId || o.clOrdId}
                        style={{
                            background: '#1E2329',
                            border: '1px solid #2B3139',
                            borderRadius: 6,
                            padding: '10px 12px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Tag color={o.side === 'BUY' ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
                                {o.side}
                            </Tag>
                            <strong style={{ color: '#EAECEF', fontSize: 14 }}>{o.symbol}</strong>
                            <Tag color={cfg.color} style={{ marginInlineEnd: 0 }}>{cfg.text}</Tag>
                            <span style={{ marginLeft: 'auto', color: '#848E9C', fontSize: 11 }}>
                                {formatTimeOnly(o.createdAt)}
                            </span>
                        </div>
                        <div style={{ color: '#EAECEF', fontSize: 13, marginBottom: 4 }}>
                            {o.price > 0 ? formatNumber(o.price) : '-'}
                            <span style={{ color: '#5E6673' }}> × </span>
                            {formatNumber(o.orderQty)}
                        </div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#848E9C', flexWrap: 'wrap' }}>
                            <span>
                                Filled <b style={{ color: o.cumQty > 0 ? '#0ECB81' : '#848E9C' }}>{formatNumber(o.cumQty)}</b>
                            </span>
                            <span>
                                Remain <b style={{ color: o.leavesQty > 0 ? '#FCD535' : '#848E9C' }}>{formatNumber(o.leavesQty)}</b>
                            </span>
                            <span>
                                Avg <b style={{ color: '#EAECEF' }}>{o.avgPx > 0 ? formatNumber(o.avgPx) : '-'}</b>
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const OrderAndTransactionsTab: React.FC<OrderAndTransactionsTabProps> = ({
    selectedPortfolio,
    active,
    refreshKey,
    pollMs,
}) => {
    const [orders, setOrders] = useState<DisplayOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const isMobile = useIsMobile();
    // The table's per-column filter dropdowns have no mobile equivalent, so side and status
    // become ordinary state that the filter sheet drives.
    const [sideFilter, setSideFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [symbolFilter, setSymbolFilter] = useState('all');

    // Pair has two legs: the stock leg (the `selectedPortfolio` passed in) and a
    // futures/deriv leg. Let the user switch which leg's orders they're viewing.
    const { portfolios: derivPortfolios } = usePortfolios({ accountType: 'DERIVATIVES' });
    const [viewPortfolioId, setViewPortfolioId] = useState<string | undefined>(undefined);

    // Default the view back to the stock leg whenever the selected pair changes.
    useEffect(() => {
        setViewPortfolioId(selectedPortfolio?.id);
    }, [selectedPortfolio?.id]);

    const portfolioOptions = useMemo(() => {
        const opts: { value: string; label: string }[] = [];
        if (selectedPortfolio?.id) {
            opts.push({ value: selectedPortfolio.id, label: `Stock — ${selectedPortfolio.portfolioName}` });
        }
        derivPortfolios.forEach((p) => {
            opts.push({ value: p.id, label: `Futures — ${p.portfolioName}` });
        });
        return opts;
    }, [selectedPortfolio?.id, selectedPortfolio?.portfolioName, derivPortfolios]);

    const effectivePortfolioId = viewPortfolioId ?? selectedPortfolio?.id;

    const fetchOrders = useCallback(async (silent = false) => {
        if (!effectivePortfolioId) {
            setOrders([]);
            return;
        }
        if (!silent) setLoading(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            const startTime = today;
            const endTime = today;

            const response = await coreService.get<OrdersResponseDto>('/order', {
                params: { startTime, endTime, portfolioId: effectivePortfolioId },
            });
            const items = response.data?.items ?? [];
            setOrders(items.map(mapOrderItem));
        } catch (error) {
            console.error('Error fetching orders:', error);
            // A failed background poll keeps what is on screen; only a user load clears it.
            if (!silent) setOrders([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [effectivePortfolioId]);

    useEffect(() => {
        if (active) fetchOrders();
    }, [active, refreshKey, fetchOrders]);

    useEffect(() => {
        if (!active || !pollMs) return;
        const id = window.setInterval(() => { void fetchOrders(true); }, pollMs);
        return () => window.clearInterval(id);
    }, [active, pollMs, fetchOrders]);

    const visibleOrders = useMemo(
        () =>
            orders.filter(
                (o) =>
                    (symbolFilter === 'all' || o.symbol === symbolFilter) &&
                    (sideFilter === 'all' || o.side === sideFilter) &&
                    (statusFilter === 'all' || o.statusCode === statusFilter),
            ),
        [orders, symbolFilter, sideFilter, statusFilter],
    );
    const orderColumns = useMemo(() => buildOrderColumns(orders), [orders]);

    if (isMobile) {
        const groups: FilterGroup[] = [
            {
                key: 'symbol',
                label: 'Symbol',
                value: symbolFilter,
                onChange: setSymbolFilter,
                options: [{ value: 'all', label: 'All symbols' }, ...symbolFiltersFor(orders).map((f) => ({ value: f.value, label: f.text }))],
            },
            {
                key: 'side',
                label: 'Side',
                value: sideFilter,
                onChange: setSideFilter,
                options: [{ value: 'all', label: 'All sides' }, ...SIDE_FILTERS.map((f) => ({ value: f.value, label: f.text }))],
            },
            {
                key: 'status',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [{ value: 'all', label: 'All statuses' }, ...statusFiltersFor(orders).map((f) => ({ value: f.value, label: f.text }))],
            },
            {
                key: 'portfolio',
                label: 'Portfolio',
                value: effectivePortfolioId ?? 'all',
                allValue: selectedPortfolio?.id ?? 'all',
                onChange: setViewPortfolioId,
                options: portfolioOptions,
            },
        ];
        // A `fill` section in the shell, which hands it the box without padding — so the
        // gutter has to come from here or the content sits flush against both edges.
        return (
            <div style={{ height: '100%', overflow: 'auto', padding: '8px 12px' }}>
                <MobileFilterBar groups={groups} />
                <MobileOrderList orders={visibleOrders} loading={loading} />
            </div>
        );
    }

    return (
        <div style={{ height: '100%', overflow: 'auto' }}>
            <style>{FILTER_DROPDOWN_STYLES}</style>
            <Section>
                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span>Orders History</span>
                            <Select
                                size="small"
                                style={{ minWidth: 220 }}
                                value={effectivePortfolioId}
                                onChange={setViewPortfolioId}
                                options={portfolioOptions}
                                placeholder="Select portfolio"
                            />
                        </div>
                    }
                >
                    <TableWrapper>
                        <StyledTableWrapper>
                            <Table
                                dataSource={orders}
                                rowKey={(record) => record.orderId || record.clOrdId}
                                loading={loading}
                                pagination={{
                                    defaultPageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                }}
                                columns={orderColumns}
                                size="middle"
                                tableLayout="auto"
                            />
                        </StyledTableWrapper>
                    </TableWrapper>
                </Card>
            </Section>
        </div>
    );
};

export default OrderAndTransactionsTab;
